import React, { useEffect, useRef, useState } from 'react';
import Formsy from 'formsy-react';
import { TextFieldFormsy } from '@fuse';
import { Button, Divider, InputAdornment, Icon, Typography } from '@material-ui/core';
import * as authActions from 'app/auth/store/actions';
import { useDispatch, useSelector } from 'react-redux';
import Error from 'app/fuse-layouts/shared-components/Error';
import * as ActionsDialog from 'app/store/actions';
import DividerWithText from '../util/DividerWithText';
import { makeStyles } from '@material-ui/core/styles';
import { Link } from 'react-router-dom';
import clsx from 'clsx';

const useStyles = makeStyles(theme => ({
	toggleButton: {
		cursor: 'pointer',
		color: theme.palette.primary.main,
		fontWeight: 500,
		'&:hover': {
			textDecoration: 'underline'
		}
	},
	inputField: {
		'& .MuiInputBase-input': {
			fontSize: '1.1rem',
			padding: '16px 14px'
		},
		'& .MuiInputLabel-outlined': {
			fontSize: '1.1rem'
		}
	},
	helperText: {
		fontSize: '0.875rem',
		color: theme.palette.text.secondary,
		marginTop: theme.spacing(0.5),
		marginBottom: theme.spacing(2)
	}
}));

const RegisterForm = props => {
	const classes = useStyles();
	const dispatch = useDispatch();
	const formRef = useRef(null);

	const error = useSelector(({ auth }) => auth.auth.error);
	const success = useSelector(({ auth }) => auth.auth.registerSuccess);
	const licenseType = useSelector(({ licenseKey }) => licenseKey.key.licenseType);

	const [isFormValid, setIsFormValid] = useState(false);

	const [changed, setChanged] = useState(false);

	// Extract campaign slug from URL path parameter
	const campaignSlug = props.match?.params?.slug;
	const campaign = props.campaign; // Campaign data passed from CampaignAuth
	const emailHelperText = campaign?.authPageCustomization?.helperText || "We'll send a verification code to this email";

	useEffect(() => {
		if (error) {
			if (formRef.current && error.type === 'form') {
				formRef.current.updateInputsWithError({
					username: error.message
				});
				disableButton();
			} else if (error.type === 'modal' && error.message) {
				dispatch(
					ActionsDialog.openDialog({
						children: <Error message={`${error.message}...`} clearError={() => dispatch(authActions.clearError())} />,
						maxWidth: 'xs',
						fullWidth: true,
						onClose: (event, reason) => {
							if (reason !== 'backdropClick') {
								dispatch(ActionsDialog.closeDialog());
							}
						}
					})
				);
			}
		}
	}, [dispatch, error]);

	useEffect(() => {
		if (success) {
			if (licenseType === 'Multi-User') {
				props.history.push({ pathname: `/verify` });
			} else {
				props.history.push({ pathname: `/login` });
			}
		}
	}, [success, props.history, licenseType]);

	useEffect(() => {
		if (error && !changed) {
			dispatch(authActions.clearError());
		}
	}, [dispatch, error, changed]);

	const disableButton = () => setIsFormValid(false);

	const enableButton = () => setIsFormValid(true);

	const handleSubmit = model => {
		delete model.passwordConfirm;
		const data = {
			...model,
			username: model.username.toLowerCase()
		};

		// Include campaign slug if present in URL
		if (campaignSlug) {
			data.slug = campaignSlug;
		}

		disableButton();
		setChanged(false);
		dispatch(authActions.submitRegister(data));
	};

	return (
		<div className="w-full">
			<Formsy
				onValidSubmit={handleSubmit}
				onValid={enableButton}
				onInvalid={disableButton}
				onChange={() => setChanged(true)}
				ref={formRef}
				className="flex flex-col justify-center w-full"
			>
				<TextFieldFormsy
					className="mb-16"
					type="text"
					id="register_form_username"
					name="username"
					label="Username"
					validations={{
						minLength: 4,
						matchRegexp: /^[a-zA-Z][a-zA-Z0-9]*$/,
						notAdmin: (values, value) => {
							return !value || (!['admin', 'root'].includes(value.toLowerCase()));
						}
					}}
					validationErrors={{
						minLength: 'Min character length is 4',
						matchRegexp: 'Use only letters and numbers. The first character must be a letter.',
						notAdmin: 'This username is restricted'
					}}
					InputProps={{
						endAdornment: (
							<InputAdornment position="end">
								<Icon className="text-20" color="action">
									person
								</Icon>
							</InputAdornment>
						)
					}}
					variant="outlined"
					required
				/>
				<TextFieldFormsy
					className={clsx("mb-16", classes.inputField)}
					type="text"
					name="firstName"
					id="register_form_firstName"
					label="First Name"
					validations={{
						minLength: 2,
						matchRegexp: /^[a-zA-Z]+$/
					}}
					validationErrors={{
						matchRegexp: 'First name contains invalid characters',
						minLength: 'Min character length is 2'
					}}
					InputProps={{
						endAdornment: (
							<InputAdornment position="end">
								<Icon className="text-20" color="action">
									person
								</Icon>
							</InputAdornment>
						)
					}}
					variant="outlined"
					required
				/>

				<TextFieldFormsy
					className={clsx("mb-16", classes.inputField)}
					type="text"
					name="lastName"
					id="register_form_lastName"
					label="Last Name"
					validations={{
						minLength: 2,
						matchRegexp: /^[a-zA-Z]+$/
					}}
					validationErrors={{
						matchRegexp: 'Last name contains invalid characters',
						minLength: 'Min character length is 2'
					}}
					InputProps={{
						endAdornment: (
							<InputAdornment position="end">
								<Icon className="text-20" color="action">
									person
								</Icon>
							</InputAdornment>
						)
					}}
					variant="outlined"
					required
				/>

				<TextFieldFormsy
					className={clsx("mb-4", classes.inputField)}
					type="text"
					name="email"
					id="register_form_email"
					label="Email"
					validations="isEmail"
					validationErrors={{
						isEmail: 'Enter a valid email'
					}}
					InputProps={{
						endAdornment: (
							<InputAdornment position="end">
								<Icon className="text-20" color="action">
									email
								</Icon>
							</InputAdornment>
						)
					}}
					variant="outlined"
					required
				/>
				<Typography className={classes.helperText}>
					{emailHelperText}
				</Typography>

				<TextFieldFormsy
					className="mb-16"
					type="password"
					name="password"
					id="register_form_password"
					label="Password"
					validations={{
						strongPass: (values, value) => {
							const strongRegex = new RegExp('^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})');
							return strongRegex.test(value);
						}
					}}
					validationErrors={{
						strongPass:
							'Must be at least 8 characters long containing at least 1 lowercase, 1 uppercase, 1 numeric, and 1 special character such as !@#$%^&*.'
					}}
					InputProps={{
						endAdornment: (
							<InputAdornment position="end">
								<Icon className="text-20" color="action">
									vpn_key
								</Icon>
							</InputAdornment>
						)
					}}
					variant="outlined"
					required
				/>

				<TextFieldFormsy
					className="mb-16"
					type="password"
					name="passwordConfirm"
					id="register_form_passwordConfirm"
					label="Confirm Password"
					validations={{
						equalsField: 'password'
					}}
					validationErrors={{
						equalsField: 'Passwords do not match'
					}}
					InputProps={{
						endAdornment: (
							<InputAdornment position="end">
								<Icon className="text-20" color="action">
									vpn_key
								</Icon>
							</InputAdornment>
						)
					}}
					variant="outlined"
					required
				/>

				<Button
					type="submit"
					variant="contained"
					color="primary"
					id="register_form_register"
					className="w-full mx-auto mt-16 normal-case"
					aria-label="REGISTER"
					disabled={!isFormValid}
					value="legacy"
				>
					Register
				</Button>
			</Formsy>

			{/* Toggle to Login or Link to Login */}
			{props.onToggleLogin ? (
				<div className="flex flex-col items-center justify-center pt-32">
					<DividerWithText text="Or" style={{ margin: '30px 0' }} />
					<Typography variant="body2" className="mt-16">
						Already have an account?
					</Typography>
					<Typography
						variant="body2"
						className={`${classes.toggleButton} mt-4`}
						onClick={props.onToggleLogin}
					>
						Sign In
					</Typography>
				</div>
			) : (
				<div className="flex flex-col items-center pt-24">
					<Divider className="my-16 w-256" />
					<Link className="font-medium" to="/login">
						Already have an account? Login
					</Link>
				</div>
			)}
		</div>
	);
};

export default RegisterForm;
